
import { Component, Vue, Ref } from 'vue-property-decorator';
import Message from '@/components/Message.vue';
import MessageInput from '@/components/MessageInput.vue';
import { Chat } from '@/store/modules';

@Component({ name: "Messaging", components: { Message, MessageInput } })
export default class Messaging extends Vue {
  @Ref('MessageInput') MessageInput!: MessageInput;
  loading = true;
  error = '';
  messages: any[] = [];
  loadingNewer = false;
  loadingOlder = false;
  loadingMessages = false;
  olderAvailable = false;
  refreshInterval = null as any;
  currentChatName = '';
  currentChatAvatarUrl = '';
  mobileChatsMenu = false;
  classesCache: any = {};
  sending = false;

  initials (name: string) {
    const initials = name.split(' ').map(a => a[0]).join('')
    return initials;
  }

  get loadingChats() {
    return Chat.loadingChats;
  }

  get chats () {
    return Chat.chats || [];
  }

  get chatsCount () {
    return Chat.chatsCount;
  }

  get chatsPage () {
    return Chat.chatsPage;
  }

  set chatsPage(value: number) {
    Chat.setChatsPage(value);
    Chat.loadChats();
  } 

  async onMessageSendButtonPressed (data: { text: string; attachments: any[] }) {
    this.sending = true;
    try {
      await Chat.sendMessageToCurrentChat(data);
      this.MessageInput.clear();
      this.fetchNewer();
      Chat.loadChats();
    } catch (err: any) {
      console.warn(err.message);
    }
    this.sending = false;
  }

  async setCurrentChat(chatId: string) {
    this.loadingMessages = true;
    this.mobileChatsMenu = false;
    this.olderAvailable = false;
    if (this.MessageInput) {
      this.MessageInput.clear();
    }
    this.messages = [];
    this.$forceUpdate();
    await Chat.setCurrentChat({ chatId });
    this.currentChatName = this.chats[this.selectedChatIndex].name;
    this.currentChatAvatarUrl = this.chats[this.selectedChatIndex].avatarUrl;
    this.fetchMessages();
    Chat.markChatAsRead(chatId);
  }

  get selectedChatIndex () {
    const found = this.chats.findIndex(chat => chat.id === this.currentChatId);

    return found;
  }

  set selectedChatIndex (index: number) {
    if (index === this.selectedChatIndex) {
      return;
    }

    const chat = this.chats[index];
    this.setCurrentChat(chat.id);
  }
  
  get currentChatId () {
    return Chat.currentChatId;
  }

  get loadingAnyMessages () {
    return this.loadingNewer || this.loadingOlder || this.loadingMessages;
  }

  async fetchMessages() {
    this.loadingMessages = true;
    try {
      const { items, count } = await Chat.getMessages({});
      this.olderAvailable = count > items.length;
      this.messages = [...items];
      this.$forceUpdate();
      clearInterval(this.refreshInterval);
      this.refreshInterval = setInterval(() => this.fetchNewer(), 3000);
    } catch (err: any) {
      console.warn(err.message);
    }
    this.loadingMessages = false;
  }

  beforeDestroy() {
    clearInterval(this.refreshInterval);
  }

  async fetchNewer() {
    if (this.loadingNewer) {
      return;
    }

    this.loadingNewer = true;
    try {
      const firstMessage = this.messages[0];
      const { items } = await Chat.getMessages(firstMessage ? { after: new Date(new Date(firstMessage.date).valueOf() + 1000)} : {});

      if (!items.length) {
        this.loadingNewer = false;
        return;
      }

      this.messages = [...items, ...this.messages];
      Chat.readCurrentChat();
      this.$forceUpdate();
    } catch (err: any) {
      console.warn(err.message);
    }
    this.loadingNewer = false;
  }

  async fetchOlder() {
    this.loadingOlder = true;
    try {
      const lastMessage = this.messages[this.messages.length - 1];

      const { items, count } = await Chat.getMessages({ before: new Date(new Date(lastMessage.date).valueOf() - 1000) });
      this.olderAvailable = count > items.length;
      this.messages.push(...items);
      this.$forceUpdate();
    } catch (err: any) {
      console.warn(err.message);
    }
    this.loadingOlder = false;
  }

  async mounted () {
    if (this.chatsPage !== 0) {
      this.chatsPage = 0;
    }

    await Chat.loadChats();
    if (this.chats.length) {
      this.setCurrentChat(this.chats[0].id);
    }

    this.$forceUpdate();
  }

  onMessageClassLoaded(data: { id: string; name: string; url: string }) {
    this.classesCache[data.id] = { name: data.name, url: data.url };
  }
}
